import React, { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import { Button, cn } from '@ideoz/ui';
import { ChevronRight, Eye, EyeOff, Loader2, MessageSquare, Redo2, Table2, Undo2, X } from 'lucide-react';
import { hotEditStore, type HotEditBridgeApi, type HotEditSelection } from '../../stores/hotEdit';
import { workbenchStore } from '../../stores/workbench';
import { hotEditHistory, stepHotEditHistory } from '../../preview-bridge/hotEditHistory';
import { pendingEdits } from '../../preview-bridge/pendingEdits';
import { ImagePicker } from './ImagePicker';
import { assetPathForDataUri } from '../../preview-bridge/imageReplace';
import { FieldsSection, MoreOptions, TextSection } from './sections';
import { isStyleSection, planPanel, repeatChipHint, type PanelSection } from './panelModel';
import {
  ALIGN_PRESETS,
  SIZE_PRESETS,
  SPACING_PRESETS,
  WEIGHT_PRESETS,
  activePreset,
  customColorClass,
  designSwatches,
  humanizeComponentName,
  type Preset,
  type Swatch,
} from './inspectorModel';

const CORNER_PRESETS: Preset[] = [
  { label: 'Square', cls: 'rounded-none', css: { borderRadius: '0px' } },
  { label: 'Soft', cls: 'rounded-md', css: { borderRadius: '0.375rem' } },
  { label: 'Round', cls: 'rounded-xl', css: { borderRadius: '0.75rem' } },
  { label: 'Pill', cls: 'rounded-full', css: { borderRadius: '9999px' } },
];

const FIT_PRESETS: Preset[] = [
  { label: 'Fill', cls: 'object-cover', css: { objectFit: 'cover' } },
  { label: 'Fit', cls: 'object-contain', css: { objectFit: 'contain' } },
];

/**
 * The prototype editor's panel (Prototype Editor UX plan).
 *
 * One column, no tabs and no modes: it shows only the sections the selected element offers, and
 * it mentions other elements only when this one really is a row of a repeated list, with the
 * real count. Exact numbers live under More options, for the founder who asks for them.
 */
export function SlimInspector({
  className,
  showCloseInHeader = true,
  onClose,
}: {
  className?: string;
  showCloseInHeader?: boolean;
  /** Closes the panel and leaves edit mode running; without it the close button ends edit mode. */
  onClose?: () => void;
}): React.ReactElement | null {
  const mode = useStore(hotEditStore.mode);
  const selection = useStore(hotEditStore.selection);
  const api = useStore(hotEditStore.bridgeApi);

  useKeyboard(api);

  if (!mode) return null;

  return (
    <aside
      className={cn('flex flex-col min-h-0 rounded-lg border border-border/60 bg-card shadow-sm overflow-hidden', className)}
      aria-label="Prototype editor"
    >
      <div className="flex items-center gap-1 px-3 py-1.5 border-b border-border/60 shrink-0">
        <span className="text-xs font-semibold text-foreground flex-1">Edit</span>
        {showCloseInHeader ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label={onClose ? 'Close this panel' : 'Close edit mode'}
            onClick={() => (onClose ? onClose() : api?.exitHotEditMode())}
          >
            <X size={14} />
          </Button>
        ) : null}
      </div>

      {!selection ? (
        <p className="flex-1 px-3 py-6 text-xs text-muted-foreground">Click anything to edit it.</p>
      ) : !api ? (
        <p className="flex-1 px-3 py-6 text-xs text-muted-foreground">Connecting to the preview...</p>
      ) : (
        <SelectionPanel selection={selection} api={api} />
      )}

      <ActionBar api={api} />
    </aside>
  );
}

function SelectionPanel({ selection, api }: { selection: HotEditSelection; api: HotEditBridgeApi }): React.ReactElement {
  const plan = planPanel(selection);
  const crumbs = selection.breadcrumb ?? [];
  const count = selection.listRow?.count ?? selection.listContainer?.count ?? 0;

  return (
    <>
      <div className="px-3 pt-2 pb-2 border-b border-border/60 space-y-1 shrink-0">
        {crumbs.length > 1 ? (
          <nav aria-label="Where this element sits" className="flex flex-wrap items-center gap-0.5 text-[11px] text-muted-foreground">
            {crumbs.slice(-3).map((c, i) => (
              <span key={c.elementId} className="flex items-center gap-0.5">
                {i > 0 ? <ChevronRight size={11} aria-hidden /> : null}
                <button
                  type="button"
                  className="rounded px-0.5 hover:text-foreground hover:bg-muted/60"
                  onClick={() => api.selectElement(c.elementId)}
                >
                  {humanizeComponentName(c.label)}
                </button>
              </span>
            ))}
          </nav>
        ) : null}
        <div className="text-sm font-semibold text-foreground">{plan.title}</div>
        {plan.repeatNote ? <p className="text-[11px] text-muted-foreground">{plan.repeatNote}</p> : null}
        {plan.holdsNote ? <p className="text-[11px] text-muted-foreground">{plan.holdsNote}</p> : null}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-4">
        {plan.blockedReason ? (
          <p className="text-xs text-muted-foreground leading-relaxed">{plan.blockedReason}</p>
        ) : (
          <>
            {plan.repeatChip && plan.sections.some(isStyleSection) ? (
              <Chip chip={plan.repeatChip} count={count} />
            ) : null}
            {plan.sections.map((section) => (
              <Section
                key={section}
                section={section}
                selection={selection}
                api={api}
                count={count}
                recordId={plan.recordId}
              />
            ))}
            {plan.sections.some(isStyleSection) ? (
              <details className="rounded-md border border-border/60">
                <summary className="cursor-pointer select-none px-2 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                  More options
                </summary>
                <div className="px-2 pb-2 pt-1">
                  <MoreOptions key={selection.elementId} selection={selection} api={api} />
                </div>
              </details>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

function Section({
  section,
  selection,
  api,
  count,
  recordId,
}: {
  section: PanelSection;
  selection: HotEditSelection;
  api: HotEditBridgeApi;
  count: number;
  recordId: string | null;
}): React.ReactElement | null {
  const [classes, setClasses] = useState(selection.className);
  useEffect(() => setClasses(selection.className), [selection.elementId, selection.className]);
  const swatches = designSwatches(selection.themeTokens);

  const pick = (preset: Preset, group: readonly Preset[]) => {
    for (const [prop, value] of Object.entries(preset.css)) api.applyInstantCss(selection.elementId, prop, value);
    api.bumpMergedFromTailwind('__hot_tw_util', preset.cls);
    api.queuePersistTypographyLayout();
    const drop = new Set(group.map((p) => p.cls));
    setClasses((c) => [...c.split(/\s+/).filter((x) => x && !drop.has(x)), preset.cls].join(' '));
  };
  const color = (cls: string, prop: string, css: string) => {
    api.applyInstantCss(selection.elementId, prop, css);
    api.bumpMergedFromTailwind('__hot_tw_util', cls);
    api.queuePersistTypographyLayout();
  };

  switch (section) {
    case 'text':
      return <TextSection key={`${selection.elementId}-text`} selection={selection} api={api} />;
    case 'fields':
      return <FieldsSection key={`${selection.elementId}-form`} selection={selection} api={api} />;
    case 'typeStyle':
      return (
        <div className="space-y-3">
          <PresetRow label="Size" presets={SIZE_PRESETS} active={activePreset(classes, SIZE_PRESETS)} onPick={(p) => pick(p, SIZE_PRESETS)} />
          <PresetRow label="Weight" presets={WEIGHT_PRESETS} active={activePreset(classes, WEIGHT_PRESETS)} onPick={(p) => pick(p, WEIGHT_PRESETS)} />
          <SwatchRow
            label="Colour"
            swatches={swatches}
            onPick={(s) => color(`text-${s.token}`, 'color', s.css)}
            onCustom={(hex) => {
              const cls = customColorClass('text', hex);
              if (cls) color(cls, 'color', hex);
            }}
          />
          <PresetRow label="Alignment" presets={ALIGN_PRESETS} active={activePreset(classes, ALIGN_PRESETS)} onPick={(p) => pick(p, ALIGN_PRESETS)} />
        </div>
      );
    case 'background':
      return (
        <div className="space-y-2">
          <SwatchRow
            label="Background"
            swatches={swatches}
            onPick={(s) => color(`bg-${s.token}`, 'backgroundColor', s.css)}
            onCustom={(hex) => {
              const cls = customColorClass('bg', hex);
              if (cls) color(cls, 'backgroundColor', hex);
            }}
          />
        </div>
      );
    case 'spacing':
      return (
        <div className="space-y-2">
          <PresetRow label="Spacing" presets={SPACING_PRESETS} active={activePreset(classes, SPACING_PRESETS)} onPick={(p) => pick(p, SPACING_PRESETS)} />
        </div>
      );
    case 'corners':
      return (
        <div className="space-y-2">
          <PresetRow label="Corners" presets={CORNER_PRESETS} active={activePreset(classes, CORNER_PRESETS)} onPick={(p) => pick(p, CORNER_PRESETS)} />
        </div>
      );
    case 'image':
      return (
        <div className="space-y-2">
          <PresetRow label="Picture fills the box" presets={FIT_PRESETS} active={activePreset(classes, FIT_PRESETS)} onPick={(p) => pick(p, FIT_PRESETS)} />
          <div className="space-y-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">Picture</span>
            <ImagePicker
              className="w-full p-0"
              current={assetPathForDataUri(workbenchStore.assets.get(), selection.imageSrc)}
              onPick={(picked) => api?.replaceImage(picked)}
            />
          </div>
        </div>
      );
    case 'icon':
      return (
        <div className="space-y-2">
          <SwatchRow
            label="Icon colour"
            swatches={swatches}
            onPick={(s) => color(`text-${s.token}`, 'color', s.css)}
            onCustom={(hex) => {
              const cls = customColorClass('text', hex);
              if (cls) color(cls, 'color', hex);
            }}
          />
        </div>
      );
    case 'visible': {
      // What the founder last did to it, not what the class list said when it was selected: the
      // button used to keep offering "Hide this" after hiding it.
      const hidden = selection.isHidden === true || classes.split(/\s+/).includes('hidden');
      return (
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-full text-xs gap-1.5"
            onClick={() => api.toggleHidden(!hidden)}
          >
            {hidden ? <Eye size={13} aria-hidden /> : <EyeOff size={13} aria-hidden />}
            {hidden ? 'Show this' : 'Hide this'}
          </Button>
        </div>
      );
    }
    case 'records':
      return recordId ? (
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-full text-xs gap-1.5"
            onClick={() => hotEditStore.dataGrid.set({ recordId })}
          >
            <Table2 size={13} aria-hidden />
            Edit list data
          </Button>
          <p className="text-[11px] text-muted-foreground">
            {count ? `All ${count} come from one table you can edit.` : 'The items come from a table you can edit.'}
          </p>
        </div>
      ) : null;
    default:
      return null;
  }
}

function Chip({ chip, count }: { chip: string | null; count: number }): React.ReactElement | null {
  if (!chip) return null;
  return (
    <span
      className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
      title={repeatChipHint(count)}
      data-testid="hot-edit-repeat-chip"
    >
      {chip}
    </span>
  );
}

/** Undo, redo, whether the change is saved, and the way to ask Ideoz. */
function ActionBar({ api }: { api: HotEditBridgeApi | null }): React.ReactElement {
  const history = useStore(hotEditHistory);
  const pending = useStore(pendingEdits);
  const save = useStore(hotEditStore.save);
  const selection = useStore(hotEditStore.selection);
  const isStreaming = useStore(workbenchStore.isStreaming);

  return (
    <div className="border-t border-border/60 px-3 py-2 shrink-0 space-y-2">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Undo"
          title="Undo (Ctrl+Z)"
          disabled={(history.undo.length === 0 && pending.length === 0) || isStreaming}
          onClick={() => {
            if (api?.undoPendingEdit()) return;
            void stepHotEditHistory(workbenchStore.webcontainer.get(), 'undo');
          }}
        >
          <Undo2 size={14} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          aria-label="Redo"
          title="Redo (Ctrl+Shift+Z)"
          disabled={history.redo.length === 0 || isStreaming}
          onClick={() => void stepHotEditHistory(workbenchStore.webcontainer.get(), 'redo')}
        >
          <Redo2 size={14} />
        </Button>
        <div className="flex-1 min-w-0 text-[11px]" role="status" data-testid="hot-edit-save-state">
          {save === 'saving' ? (
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Saving...
            </span>
          ) : save === 'saved' ? (
            <span className="text-muted-foreground">Saved</span>
          ) : save === 'failed' ? (
            <span className="inline-flex items-center gap-1 text-red-700">
              Not saved
              <button type="button" className="underline hover:no-underline" onClick={() => api?.retryLastSave()}>
                Retry
              </button>
            </span>
          ) : null}
        </div>
      </div>
      {selection ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full h-7 justify-start px-1 text-[11px] text-muted-foreground gap-1.5"
          onClick={() => api?.askIdeoz()}
        >
          <MessageSquare size={12} aria-hidden />
          Ask Ideoz about this element
        </Button>
      ) : null}
    </div>
  );
}

function PresetRow({
  label,
  presets,
  active,
  onPick,
}: {
  label: string;
  presets: readonly Preset[];
  active: Preset | undefined;
  onPick: (preset: Preset) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
        {presets.map((p) => (
          <button
            key={p.cls}
            type="button"
            aria-pressed={active?.cls === p.cls}
            className={cn(
              'rounded-md border px-2 py-1 text-xs transition-colors',
              active?.cls === p.cls
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-background text-foreground hover:bg-muted',
            )}
            onClick={() => onPick(p)}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SwatchRow({
  label,
  swatches,
  onPick,
  onCustom,
}: {
  label: string;
  swatches: Swatch[];
  onPick: (swatch: Swatch) => void;
  onCustom: (hex: string) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1.5">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={label}>
        {swatches.map((s) => (
          <button
            key={s.token}
            type="button"
            title={s.label}
            aria-label={`${label}: ${s.label}`}
            className="h-6 w-6 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
            style={{ background: s.css }}
            onClick={() => onPick(s)}
          />
        ))}
        <label className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Any other colour">
          <input
            type="color"
            aria-label={`${label}: custom colour`}
            className="h-6 w-6 cursor-pointer rounded border border-border bg-background"
            onChange={(e) => onCustom(e.target.value)}
          />
          Custom
        </label>
      </div>
    </div>
  );
}

/** Esc, arrow-up, undo and redo while the focus is not in a field. */
function useKeyboard(api: HotEditBridgeApi | null): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (!hotEditStore.mode.get() || !api) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || /^(input|textarea|select)$/i.test(target.tagName))) return;
      if (target?.closest('.cm-editor')) return;
      const mod = e.ctrlKey || e.metaKey;
      if (e.key === 'Escape' && hotEditStore.selection.get()) {
        e.preventDefault();
        api.deselect();
      } else if (e.key === 'ArrowUp' && !mod && hotEditStore.selection.get()) {
        e.preventDefault();
        api.selectParent();
      } else if (mod && (e.key === 'z' || e.key === 'Z')) {
        if (workbenchStore.isStreaming.get()) return;
        e.preventDefault();
        if (!e.shiftKey && api.undoPendingEdit()) return;
        void stepHotEditHistory(workbenchStore.webcontainer.get(), e.shiftKey ? 'redo' : 'undo');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [api]);
}
